// Shared React-Select styles for consistent design across the system
export const customSelectStyles = {
    control: (provided, state) => {
        // Check if on mobile device (width <= 768px)
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        
        return {
            ...provided,
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            minHeight: '42px',
            boxShadow: 'none',
            backgroundColor: '#ffffff',
            fontFamily: '"FuturaPT-Book", sans-serif',
            fontSize: '14px',
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            '&:hover': {
                borderColor: isMobile ? '#d1d5db' : '#f97316',
            },
            '&:focus-within': {
                borderColor: isMobile ? '#d1d5db' : '#f97316',
                // Remove orange glow on focus
                boxShadow: 'none',
            },
        };
    },
    menu: (provided) => ({
        ...provided,
        zIndex: 99999,
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#ffffff',
        marginTop: '4px',
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 99999,
    }),
    menuList: (provided) => ({
        ...provided,
        maxHeight: '200px',
        padding: 0,
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#f97316' : (state.isFocused ? '#f3f4f6' : '#ffffff'),
        color: state.isSelected ? '#ffffff' : '#000000',
        fontFamily: '"FuturaPT-Book", sans-serif',
        fontSize: '14px',
        padding: '10px 12px',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: state.isSelected ? '#f97316' : '#f3f4f6',
            color: state.isSelected ? '#ffffff' : '#000000',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#9ca3af',
        fontFamily: '"FuturaPT-Book", sans-serif',
        fontSize: '14px',
    }),
    singleValue: (provided) => ({
        ...provided,
        color: '#374151',
        fontFamily: '"FuturaPT-Book", sans-serif',
        fontSize: '14px',
    }),
    multiValue: (provided) => {
        // Check if on mobile device (width <= 768px)
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        
        return {
            ...provided,
            backgroundColor: '#e0f2fe',
            borderRadius: '4px',
            margin: '2px',
            maxWidth: isMobile ? '100%' : 'none',
            display: 'flex',
            alignItems: 'center',
        };
    },
    multiValueLabel: (provided) => {
        // Check if on mobile device (width <= 768px)
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        
        return {
            ...provided,
            color: '#0369a1',
            fontFamily: '"FuturaPT-Book", sans-serif',
            fontSize: isMobile ? '14px' : '13px',
            padding: isMobile ? '4px 6px' : '3px 6px',
            paddingLeft: '8px',
            // Ensure text is visible on mobile
            overflow: 'visible',
            textOverflow: 'clip',
            whiteSpace: 'nowrap',
        };
    },
    multiValueRemove: (provided) => ({
        ...provided,
        color: '#0369a1',
        paddingLeft: '4px',
        paddingRight: '4px',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#bae6fd',
            color: '#0c4a6e',
        },
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        color: '#6b7280',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
    }),
    valueContainer: (provided) => {
        // Check if on mobile device (width <= 768px)
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        
        return {
            ...provided,
            padding: isMobile ? '4px 8px' : '0 12px',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            overflow: 'visible',
            flex: '1 1 auto',
            minWidth: 0, // Allow flex to shrink properly
            gap: isMobile ? '4px' : '0',
        };
    },
    input: (provided) => {
        // Check if on mobile device (width <= 768px)
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        
        return {
            ...provided,
            margin: 0,
            padding: 0,
            color: '#374151',
            fontFamily: '"FuturaPT-Book", sans-serif',
            fontSize: isMobile ? '16px' : '14px', // 16px on mobile prevents zoom
            opacity: 1,
            visibility: 'visible',
            flex: 1,
            minWidth: isMobile ? '120px' : 'auto',
            width: '100%',
            // Ensure input is visible and functional on mobile
            '& input': {
                font: 'inherit !important',
                color: '#374151 !important',
                fontSize: isMobile ? '16px !important' : '14px !important',
                lineHeight: '20px !important',
                height: 'auto !important',
                opacity: '1 !important',
                visibility: 'visible !important',
                caretColor: '#374151 !important',
                WebkitTextFillColor: '#374151 !important',
                WebkitOpacity: '1 !important',
                width: '100% !important',
                minWidth: '120px !important',
            },
        };
    },
};

// Common Select props for consistency
export const commonSelectProps = {
    styles: customSelectStyles,
    isClearable: false,
    isSearchable: true,
    menuPosition: "fixed",
    menuPlacement: "auto",
    classNamePrefix: "react-select",
};

// Awning accordion select styles - matches input field design
export const awningSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        border: state.isFocused ? '1.5px solid #f97316' : '1.5px solid #e5e7eb',
        borderRadius: '8px',
        minHeight: '44px',
        height: '44px',
        boxShadow: 'none',
        backgroundColor: state.isFocused ? '#ffffff' : '#f9fafb',
        fontFamily: '"FuturaPT-Book", sans-serif',
        fontSize: '14px',
        padding: '0',
        outline: 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#f97316' : '#d1d5db',
            backgroundColor: '#ffffff',
        },
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 99999,
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#ffffff',
        marginTop: '4px',
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 99999,
    }),
    menuList: (provided) => ({
        ...provided,
        maxHeight: '200px',
        padding: 0,
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#f97316' : (state.isFocused ? '#f3f4f6' : '#ffffff'),
        color: state.isSelected ? '#ffffff' : '#000000',
        fontFamily: '"FuturaPT-Book", sans-serif',
        fontSize: '14px',
        padding: '10px 12px',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: state.isSelected ? '#f97316' : '#f3f4f6',
            color: state.isSelected ? '#ffffff' : '#000000',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#9ca3af',
        fontFamily: '"FuturaPT-Book", sans-serif',
        fontSize: '14px',
        marginLeft: '0',
        border: 'none',
        outline: 'none',
    }),
    singleValue: (provided) => ({
        ...provided,
        color: '#374151',
        fontFamily: '"FuturaPT-Book", sans-serif',
        fontSize: '14px',
        marginLeft: '0',
        border: 'none',
        outline: 'none',
        boxShadow: 'none',
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        color: '#6b7280',
        padding: '0 8px',
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: '0 14px',
        overflow: 'visible',
        border: 'none',
        outline: 'none',
        boxShadow: 'none',
        backgroundColor: 'transparent',
    }),
    input: (provided) => ({
        ...provided,
        margin: 0,
        padding: 0,
        color: '#374151',
        fontFamily: '"FuturaPT-Book", sans-serif',
        fontSize: '14px',
        border: 'none',
        outline: 'none',
        boxShadow: 'none',
        backgroundColor: 'transparent',
        '& input': {
            border: 'none !important',
            outline: 'none !important',
            boxShadow: 'none !important',
            backgroundColor: 'transparent !important',
        },
    }),
    inputContainer: () => ({
        margin: 0,
        padding: 0,
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: 'none',
        minWidth: 0,
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: '0 4px',
        color: '#6b7280',
    }),
    clearIndicator: (provided) => ({
        ...provided,
        padding: '0 4px',
        color: '#6b7280',
    }),
}; 